import initSqlJs, { Database, SqlJsStatic, QueryExecResult } from 'sql.js';
import sqlWasmBrowserUrl from 'sql.js/dist/sql-wasm-browser.wasm?url';
import { 
  UserAccount, 
  ContractedPlace, 
  BillInvoice, 
  EnergyDataPoint, 
  InAppNotification, 
  PushAlertConfig,
  PortalCredential,
  ScrapeLogEntry
} from '../types/energy';
import { BiometricConfig } from './biometricService';
import { activeRosterPlaceId } from './portalPlaces';
import {
  isStaleWasmIdbEntry,
  isStaleWasmOpfsEntry,
  wasmCacheId,
  wasmIdbKey
} from './sqliteWasmCache';
import { 
  INITIAL_ACCOUNT, 
  REAL_CONTRACTED_PLACES, 
  DEMO_CONTRACTED_PLACES,
  getPlaceData,
  INITIAL_INVOICES, 
  generateHourlyUsage, 
  generateDailyUsage, 
  generateMonthlyUsage,
  DEFAULT_TARIFF
} from './mockData';

const OPFS_DB_FILE = 'symenergy_sqlite_v4_real.db';
const IDB_NAME = 'symenergy_sqlite_wasm_v4_real';
const IDB_STORE = 'sqlite_storage';
const IDB_KEY = 'sqlite_db_binary';

export interface SQLiteStats {
  ready: boolean;
  version: string;
  sizeBytes: number;
  tables: { name: string; count: number }[];
  lastPersistAt: string;
  storageBackend: string;
  storageLocation: string;
  isOpfsActive: boolean;
}

// In-memory singleton instance of SQLite WASM database
let sqlInstance: SqlJsStatic | null = null;
let dbInstance: Database | null = null;
let initPromise: Promise<Database> | null = null;
let lastPersistTimestamp = new Date().toISOString();

let opfsSupported: boolean | null = null;
let activeStorageBackend: 'OPFS (Origin Private File System)' | 'IndexedDB (Fallback)' = 'IndexedDB (Fallback)';

/**
 * Checks if the browser environment supports and allows OPFS (Origin Private File System).
 * When running in standard browser contexts or standalone PWA, OPFS provides direct high-speed disk I/O.
 */
export async function checkOpfsSupport(): Promise<boolean> {
  if (opfsSupported !== null) return opfsSupported;
  try {
    if (typeof navigator !== 'undefined' && navigator.storage && typeof navigator.storage.getDirectory === 'function') {
      const root = await navigator.storage.getDirectory();
      const testHandle = await root.getFileHandle('.opfs_probe', { create: true });
      if (typeof testHandle.createWritable === 'function') {
        const w = await testHandle.createWritable();
        await w.write(new Uint8Array([0x53, 0x51, 0x4c]) as unknown as BufferSource); // 'SQL'
        await w.close();
        await root.removeEntry('.opfs_probe');
      }
      opfsSupported = true;
      activeStorageBackend = 'OPFS (Origin Private File System)';
      console.log('[SQLite WASM] OPFS (Origin Private File System) is fully supported and active');
      return true;
    }
  } catch (err) {
    console.warn('[SQLite WASM] OPFS probe unavailable or restricted in this context, using IndexedDB fallback:', err);
  }
  opfsSupported = false;
  activeStorageBackend = 'IndexedDB (Fallback)';
  return false;
}

// Helper: Read SQLite binary from OPFS (Origin Private File System)
async function readBinaryFromOPFS(): Promise<Uint8Array | null> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return null;
    const root = await navigator.storage.getDirectory();
    const fileHandle = await root.getFileHandle(OPFS_DB_FILE, { create: false });
    const file = await fileHandle.getFile();
    if (!file || file.size === 0) return null;
    const buf = await file.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

// Helper: Write SQLite binary to OPFS (Origin Private File System)
async function writeBinaryToOPFS(binary: Uint8Array): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) return false;
    const root = await navigator.storage.getDirectory();
    const fileHandle = await root.getFileHandle(OPFS_DB_FILE, { create: true });
    if (typeof fileHandle.createWritable === 'function') {
      const writable = await fileHandle.createWritable();
      await writable.write(binary as unknown as BufferSource);
      await writable.close();
      lastPersistTimestamp = new Date().toISOString();
      return true;
    }
    return false;
  } catch (err) {
    console.warn('[SQLite WASM] OPFS write error:', err);
    return false;
  }
}

// Helper: Open IndexedDB for SQLite WASM binary persistence (Fallback / Secondary backup)
function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB not supported in this environment'));
    }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Helper: Read SQLite binary from IndexedDB
async function readBinaryFromIDB(): Promise<Uint8Array | null> {
  try {
    const idb = await openIndexedDB();
    return new Promise((resolve) => {
      const tx = idb.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => {
        if (req.result instanceof Uint8Array) {
          resolve(req.result);
        } else if (req.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(req.result));
        } else {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    });
  } catch (err) {
    console.warn('[SQLite WASM] IDB read failed, will start fresh memory DB', err);
    return null;
  }
}

// Helper: Write SQLite binary to IndexedDB
async function writeBinaryToIDB(binary: Uint8Array): Promise<void> {
  try {
    const idb = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = idb.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(binary, IDB_KEY);
      req.onsuccess = () => {
        lastPersistTimestamp = new Date().toISOString();
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[SQLite WASM] IDB write failed', err);
  }
}

/**
 * Universal Storage Reader: Prioritizes OPFS, seamlessly migrating from IndexedDB if found.
 */
async function readBinaryFromStorage(): Promise<Uint8Array | null> {
  const isOpfs = await checkOpfsSupport();
  if (isOpfs) {
    const opfsData = await readBinaryFromOPFS();
    if (opfsData && opfsData.length > 0) {
      console.log(`[SQLite WASM] Loaded SQLite DB from OPFS (opfs://${OPFS_DB_FILE}, ${opfsData.byteLength} bytes)`);
      return opfsData;
    }
    // Check if previous data exists in IndexedDB and migrate to OPFS!
    const idbData = await readBinaryFromIDB();
    if (idbData && idbData.length > 0) {
      console.log(`[SQLite WASM] Migrating existing database from IndexedDB to OPFS (${idbData.byteLength} bytes)...`);
      await writeBinaryToOPFS(idbData);
      return idbData;
    }
    return null;
  }
  return await readBinaryFromIDB();
}

/**
 * Universal Storage Writer: Prioritizes OPFS with background mirroring to IndexedDB.
 */
async function writeBinaryToStorage(binary: Uint8Array): Promise<void> {
  const isOpfs = await checkOpfsSupport();
  if (isOpfs) {
    const success = await writeBinaryToOPFS(binary);
    if (success) {
      // Mirror to IndexedDB as secondary resilience backup
      writeBinaryToIDB(binary).catch(() => {});
      return;
    }
  }
  await writeBinaryToIDB(binary);
}

// Initialize SQLite schema
function createTablesIfNotExist(db: Database) {
  db.run(`
    CREATE TABLE IF NOT EXISTS app_settings (
      key TEXT PRIMARY KEY,
      value TEXT NOT NULL,
      updated_at TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS accounts (
      account_number TEXT PRIMARY KEY,
      account_holder TEXT NOT NULL,
      service_address TEXT,
      contract_capacity TEXT,
      payment_details TEXT,
      current_place_id TEXT,
      raw_json TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS contracted_places (
      id TEXT PRIMARY KEY,
      demand_number TEXT NOT NULL,
      plan_name TEXT NOT NULL,
      status TEXT NOT NULL,
      address TEXT NOT NULL,
      contract_capacity TEXT,
      display_name TEXT NOT NULL,
      raw_json TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS invoices (
      id TEXT PRIMARY KEY,
      place_id TEXT,
      invoice_number TEXT NOT NULL,
      period_start TEXT NOT NULL,
      period_end TEXT NOT NULL,
      kwh_used REAL NOT NULL,
      total_amount INTEGER NOT NULL,
      due_date TEXT NOT NULL,
      status TEXT NOT NULL,
      raw_json TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS energy_telemetry (
      id TEXT PRIMARY KEY,
      place_id TEXT,
      period_type TEXT NOT NULL,
      label TEXT NOT NULL,
      timestamp TEXT,
      kwh REAL NOT NULL,
      cost INTEGER NOT NULL,
      is_peak INTEGER DEFAULT 0,
      day_kwh REAL,
      life_kwh REAL,
      night_kwh REAL,
      raw_json TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS notifications (
      id TEXT PRIMARY KEY,
      title TEXT NOT NULL,
      body TEXT NOT NULL,
      type TEXT NOT NULL,
      timestamp TEXT NOT NULL,
      read_flag INTEGER DEFAULT 0,
      amount INTEGER
    );

    CREATE TABLE IF NOT EXISTS biometric_config (
      id TEXT PRIMARY KEY,
      enabled INTEGER DEFAULT 0,
      biometric_type TEXT DEFAULT 'touch_id',
      pin_code TEXT DEFAULT '1234',
      auto_lock_minutes INTEGER DEFAULT 5
    );

    CREATE TABLE IF NOT EXISTS scraper_credentials (
      id TEXT PRIMARY KEY,
      portal_url TEXT NOT NULL,
      dashboard_url TEXT NOT NULL,
      email TEXT NOT NULL,
      password TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS scraper_logs (
      id TEXT PRIMARY KEY,
      timestamp TEXT NOT NULL,
      step TEXT NOT NULL,
      status TEXT NOT NULL,
      details TEXT NOT NULL
    );

    CREATE INDEX IF NOT EXISTS idx_invoices_place ON invoices(place_id);
    CREATE INDEX IF NOT EXISTS idx_telemetry_period ON energy_telemetry(period_type, place_id);
  `);
}

// Persist the current SQLite database state
export async function persistDatabase(): Promise<void> {
  if (!dbInstance) return;
  try {
    const binary = dbInstance.export();
    await writeBinaryToStorage(binary);
  } catch (err) {
    console.error('[SQLite WASM] Failed to export and persist SQLite database', err);
  }
}

// The binary must come from the installed sql.js build: the browser glue binds emscripten exports by
// letter, so a foreign release throws "Qt.O is not a function" and leaves initSqlJs pending forever.
// The cache id is the fingerprinted asset name — see sqliteWasmCache.ts.
const SQL_WASM_BINARY_URL = sqlWasmBrowserUrl;
const SQL_WASM_CACHE_ID = wasmCacheId(SQL_WASM_BINARY_URL);

// Verify that a buffer starts with standard WebAssembly magic header (\0asm: 0x00, 0x61, 0x73, 0x6d)
function isValidWasmHeader(buf: ArrayBuffer | Uint8Array | ArrayBufferLike): boolean {
  if (!buf || buf.byteLength < 4) return false;
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf as ArrayBuffer, 0, 4);
  return bytes[0] === 0x00 && bytes[1] === 0x61 && bytes[2] === 0x73 && bytes[3] === 0x6d;
}

// Read cached WASM binary for this build only, from OPFS or IndexedDB
async function readWasmBinaryFromStorage(id: string): Promise<ArrayBuffer | null> {
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
      const root = await navigator.storage.getDirectory();
      const fileHandle = await root.getFileHandle(id, { create: false });
      const file = await fileHandle.getFile();
      if (file && file.size > 0) {
        const buf = await file.arrayBuffer();
        if (isValidWasmHeader(buf)) {
          return buf;
        }
      }
    }
  } catch {}

  try {
    const idb = await openIndexedDB();
    return new Promise((resolve) => {
      const tx = idb.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(wasmIdbKey(id));
      req.onsuccess = () => {
        const val = req.result;
        if (val instanceof ArrayBuffer && isValidWasmHeader(val)) {
          resolve(val);
        } else if (val instanceof Uint8Array && isValidWasmHeader(val)) {
          const ab = new ArrayBuffer(val.byteLength);
          new Uint8Array(ab).set(val);
          resolve(ab);
        } else {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

// Cache valid WASM binary into OPFS and IndexedDB for offline and instantaneous boot
async function writeWasmBinaryToStorage(id: string, binary: ArrayBuffer): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
      const root = await navigator.storage.getDirectory();
      const fileHandle = await root.getFileHandle(id, { create: true });
      if (typeof fileHandle.createWritable === 'function') {
        const writable = await fileHandle.createWritable();
        await writable.write(binary);
        await writable.close();
      }
    }
  } catch {}

  try {
    const idb = await openIndexedDB();
    const tx = idb.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    store.put(binary, wasmIdbKey(id));
  } catch (err) {
    console.warn('[SQLite WASM] Failed to cache wasm binary to IDB', err);
  }
}

// Delete cached binaries that this build did not write, so a rebuilt sql.js can never read back the
// previous release's binary. The database snapshot and every other OPFS/IDB entry are left alone.
async function pruneStaleWasmCaches(id: string): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
      const root = await navigator.storage.getDirectory();
      const names: string[] = [];
      for await (const name of root.keys()) names.push(name);
      for (const name of names) {
        if (isStaleWasmOpfsEntry(name, id)) {
          await root.removeEntry(name);
        }
      }
    }
  } catch (err) {
    console.warn('[SQLite WASM] Failed to prune stale OPFS wasm binaries', err);
  }

  try {
    const idb = await openIndexedDB();
    await new Promise<void>((resolve) => {
      const tx = idb.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        for (const key of req.result) {
          if (isStaleWasmIdbEntry(String(key), id)) {
            store.delete(key);
          }
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch (err) {
    console.warn('[SQLite WASM] Failed to prune stale IndexedDB wasm binaries', err);
  }
}

// Load the WASM binary paired with the bundled sql.js glue: this build's cache first, then the
// fingerprinted build asset. There is deliberately no CDN fallback — fetching a different sql.js
// release is what crashed production boot, and a missing asset must surface as a reported failure.
async function loadSqlWasmBinary(): Promise<ArrayBuffer> {
  const cached = await readWasmBinaryFromStorage(SQL_WASM_CACHE_ID);
  if (cached) {
    console.log('[SQLite WASM] Loaded pre-cached WebAssembly binary matching this build');
    return cached;
  }

  console.log(`[SQLite WASM] Loading WebAssembly binary from build asset: ${SQL_WASM_BINARY_URL}`);
  const res = await fetch(SQL_WASM_BINARY_URL, {
    headers: { 'Accept': 'application/wasm, */*' }
  });

  if (!res.ok) {
    throw new Error(`Build asset ${SQL_WASM_BINARY_URL} returned HTTP ${res.status} ${res.statusText}`);
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  // Explicitly reject HTML responses (e.g. 404 / 502 SPA fallback text/html)
  if (contentType.includes('text/html')) {
    throw new Error(`Received HTML response with MIME type "${contentType}" instead of WebAssembly binary`);
  }

  const buffer = await res.arrayBuffer();
  if (!isValidWasmHeader(buffer)) {
    throw new Error('Build asset does not start with WebAssembly magic header (\\0asm)');
  }

  console.log(`[SQLite WASM] Verified WebAssembly binary for this build (${buffer.byteLength} bytes)`);
  await pruneStaleWasmCaches(SQL_WASM_CACHE_ID);
  await writeWasmBinaryToStorage(SQL_WASM_CACHE_ID, buffer);
  return buffer;
}

// sql.js memoizes its init promise module-wide, so a glue that throws during instantiation leaves that
// promise pending for the rest of the session. Without a deadline the boot would hang and
// getSqliteInitFailure() would report nothing, leaving an empty dashboard indistinguishable from a
// broken storage engine.
const SQL_WASM_INIT_TIMEOUT_MS = 30000;

function initSqlJsPairedWithBinary(wasmBinary: ArrayBuffer): Promise<SqlJsStatic> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new Error(
          `sql.js did not finish initializing within ${SQL_WASM_INIT_TIMEOUT_MS}ms; the WebAssembly binary and the sql.js glue are probably from different builds`
        )
      );
    }, SQL_WASM_INIT_TIMEOUT_MS);

    initSqlJs({ wasmBinary }).then(
      (instance) => {
        clearTimeout(timer);
        resolve(instance);
      },
      (err) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    );
  });
}

// Set when opening the persisted snapshot failed, so callers can tell an empty database
// apart from a storage engine that never came up. Cleared on the next successful init.
let lastInitFailure: string | null = null;

export function getSqliteInitFailure(): string | null {
  return lastInitFailure;
}

// Get or initialize SQLite WASM Database
export async function getSqliteDatabase(): Promise<Database> {
  if (dbInstance) return dbInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      console.log('[SQLite WASM] Initializing SQLite WebAssembly engine...');
      if (!sqlInstance) {
        sqlInstance = await initSqlJsPairedWithBinary(await loadSqlWasmBinary());
      }

      const existingBinary = await readBinaryFromStorage();
      if (existingBinary && existingBinary.length > 0) {
        console.log(`[SQLite WASM] Loaded existing SQLite database (${existingBinary.byteLength} bytes)`);
        dbInstance = new sqlInstance.Database(existingBinary);
      } else {
        console.log('[SQLite WASM] Creating fresh in-memory SQLite database');
        dbInstance = new sqlInstance.Database();
      }

      createTablesIfNotExist(dbInstance);

      // Check if seeded; if not, seed base settings
      const checkRes = dbInstance.exec("SELECT COUNT(*) as cnt FROM app_settings WHERE key = 'lang'");
      const count = checkRes.length > 0 && checkRes[0].values[0] ? Number(checkRes[0].values[0][0]) : 0;

      if (count === 0) {
        console.log('[SQLite WASM] Initializing clean application settings...');
        await seedInitialData(dbInstance);
      }

      // Check for legacy localStorage data to migrate and remove
      migrateFromLocalStorage(dbInstance);

      await persistDatabase();
      lastInitFailure = null;
      return dbInstance;
    } catch (err) {
      console.error('[SQLite WASM] Fatal error initializing SQLite WASM:', err);
      initPromise = null;
      lastInitFailure = err instanceof Error ? err.message : String(err);
      // Fallback in-memory DB if IDB or WASM throws
      if (sqlInstance) {
        dbInstance = new sqlInstance.Database();
        createTablesIfNotExist(dbInstance);
        await seedInitialData(dbInstance);
        return dbInstance;
      }
      throw err;
    }
  })();

  return initPromise;
}

// Seed Initial Dataset (Clean defaults for production publishing)
async function seedInitialData(db: Database) {
  // 1. Initial Biometrics
  db.run(
    `INSERT OR REPLACE INTO biometric_config (id, enabled, biometric_type, pin_code, auto_lock_minutes)
     VALUES (?, ?, ?, ?, ?)`,
    ['default', 0, 'touch_id', '1234', 5]
  );

  // 2. Initial App Settings
  const now = new Date().toISOString();
  db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES (?, ?, ?)`, ['lang', 'ja', now]);
  db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES (?, ?, ?)`, ['selected_place_id', '', now]);
  db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES (?, ?, ?)`, ['last_sync_at', '', now]);
  db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES (?, ?, ?)`, ['auto_sync_enabled', 'false', now]);

  // 3. Preserve saved credentials if present in localStorage
  let savedEmail = '';
  let savedPw = '';
  try {
    if (typeof localStorage !== 'undefined') {
      const rawCreds = localStorage.getItem('symenergy_portal_credentials_v3');
      const rawPw = localStorage.getItem('symenergy_portal_password_v3');
      if (rawCreds) {
        const parsed = JSON.parse(rawCreds);
        if (parsed?.email) savedEmail = parsed.email;
      }
      if (rawPw) savedPw = rawPw;
    }
  } catch {}

  const cleanAccount: UserAccount = {
    ...INITIAL_ACCOUNT,
    accountNumber: savedEmail,
    accountHolder: savedEmail
  };

  db.run(
    `INSERT OR REPLACE INTO accounts (account_number, account_holder, service_address, contract_capacity, payment_details, current_place_id, raw_json)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [savedEmail, savedEmail, '', '', '', '', JSON.stringify(cleanAccount)]
  );

  db.run(
    `INSERT OR REPLACE INTO scraper_credentials (id, portal_url, dashboard_url, email, password)
     VALUES (?, ?, ?, ?, ?)`,
    [
      'primary',
      'https://www.symenergy.net/symenergy/login',
      'https://www.symenergy.net/symenergy/dashboard',
      savedEmail,
      savedPw
    ]
  );
}

// Migrate any lingering data from localStorage and clean it out
function migrateFromLocalStorage(db: Database) {
  if (typeof localStorage === 'undefined') return;

  try {
    const lang = localStorage.getItem('sym_energy_lang');
    if (lang === 'en' || lang === 'ja') {
      db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES ('lang', ?, ?)`, [lang, new Date().toISOString()]);
    }

    const legacySelectedPlace = localStorage.getItem('symenergy_selected_place_id');
    if (legacySelectedPlace) {
      db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES ('selected_place_id', ?, ?)`, [legacySelectedPlace, new Date().toISOString()]);
    }

    // Now safely clear localStorage since data is stored locally in SQLite WASM
    localStorage.removeItem('symenergy_places_v2');
    localStorage.removeItem('symenergy_selected_place_id');
    localStorage.removeItem('symenergy_account_v2');
    localStorage.removeItem('symenergy_invoices_v2');
    localStorage.removeItem('symenergy_hourly_v2');
    localStorage.removeItem('symenergy_daily_v2');
    localStorage.removeItem('symenergy_monthly_v2');
    localStorage.removeItem('symenergy_portal_credentials');
    localStorage.removeItem('symenergy_portal_password');
    localStorage.removeItem('symenergy_portal_logs');
    localStorage.removeItem('symenergy_biometric_config');
    localStorage.removeItem('symenergy_push_config');
    localStorage.removeItem('symenergy_inapp_notifications');
    console.log('[SQLite WASM] Successfully migrated and transitioned storage to SQLite WASM.');
  } catch (err) {
    console.warn('[SQLite WASM] Migration check notice', err);
  }
}

// ---------------------------------------------------------------------------
// High-Level Data Access Methods (Typed SQLite Operations)
// ---------------------------------------------------------------------------

export interface LocalSyncBundle {
  account: UserAccount;
  places: ContractedPlace[];
  selectedPlaceId: string;
  invoices: BillInvoice[];
  hourlyUsage: EnergyDataPoint[];
  dailyUsage: EnergyDataPoint[];
  monthlyUsage: EnergyDataPoint[];
  lastSyncAt: string;
  autoSyncEnabled: boolean;
}

// Load entire bundle from SQLite
export async function loadSqliteBundle(overridePlaceId?: string): Promise<LocalSyncBundle> {
  let db: Database;
  try {
    db = await getSqliteDatabase();
  } catch (err) {
    console.warn('[SQLite WASM] loadSqliteBundle using fallback bundle due to init error:', err);
    return {
      account: INITIAL_ACCOUNT,
      places: [],
      selectedPlaceId: '',
      invoices: [],
      hourlyUsage: [],
      dailyUsage: [],
      monthlyUsage: [],
      lastSyncAt: '',
      autoSyncEnabled: false
    };
  }

  // 1. App settings
  let selectedPlaceId = overridePlaceId || '';
  let lastSyncAt = '';
  let autoSyncEnabled = false;

  const settingsRows = db.exec('SELECT key, value FROM app_settings');
  if (settingsRows.length > 0) {
    for (const row of settingsRows[0].values) {
      const k = String(row[0]);
      const v = String(row[1]);
      if (k === 'selected_place_id' && !overridePlaceId) selectedPlaceId = v;
      if (k === 'last_sync_at') lastSyncAt = v;
      if (k === 'auto_sync_enabled') autoSyncEnabled = v === 'true';
    }
  }

  // 2. Contracted places
  const placesRows = db.exec('SELECT raw_json FROM contracted_places ORDER BY id ASC');
  const places: ContractedPlace[] = [];
  if (placesRows.length > 0) {
    for (const row of placesRows[0].values) {
      try {
        places.push(JSON.parse(String(row[0])));
      } catch {
        // ignore
      }
    }
  }

  const finalPlaces: ContractedPlace[] = places;
  if ((!selectedPlaceId || !finalPlaces.some((p: ContractedPlace) => p.id === selectedPlaceId)) && finalPlaces.length > 0) {
    // Telemetry is keyed by place_id, so guessing wrong here reads back zero rows from a
    // fully populated database. The roster status is the portal's own answer.
    selectedPlaceId = activeRosterPlaceId(finalPlaces) || finalPlaces[0].id;
  }

  // 3. Account
  const accountRows = db.exec('SELECT raw_json FROM accounts LIMIT 1');
  let account: UserAccount = INITIAL_ACCOUNT;
  if (accountRows.length > 0 && accountRows[0].values[0]) {
    try {
      account = JSON.parse(String(accountRows[0].values[0][0]));
    } catch {
      // ignore
    }
  }
  account.currentPlaceId = selectedPlaceId;
  account.contractedPlaces = finalPlaces;

  // 4. Invoices (filtered by place_id = selectedPlaceId or all if selectedPlaceId is empty)
  const invoicesRows = selectedPlaceId
    ? db.exec('SELECT raw_json FROM invoices WHERE place_id = ? ORDER BY due_date DESC', [selectedPlaceId])
    : db.exec('SELECT raw_json FROM invoices ORDER BY due_date DESC');
  let invoices: BillInvoice[] = [];
  if (invoicesRows.length > 0) {
    for (const row of invoicesRows[0].values) {
      try {
        invoices.push(JSON.parse(String(row[0])));
      } catch {
        // ignore
      }
    }
  }

  // 5. Telemetry (filtered by place_id = selectedPlaceId or all if selectedPlaceId is empty)
  const hourlyRows = selectedPlaceId
    ? db.exec("SELECT raw_json FROM energy_telemetry WHERE period_type = 'hourly' AND place_id = ? ORDER BY timestamp ASC", [selectedPlaceId])
    : db.exec("SELECT raw_json FROM energy_telemetry WHERE period_type = 'hourly' ORDER BY timestamp ASC");
  let hourlyUsage: EnergyDataPoint[] = [];
  if (hourlyRows.length > 0) {
    for (const row of hourlyRows[0].values) {
      try {
        hourlyUsage.push(JSON.parse(String(row[0])));
      } catch {
        // ignore
      }
    }
  }

  const dailyRows = selectedPlaceId
    ? db.exec("SELECT raw_json FROM energy_telemetry WHERE period_type = 'daily' AND place_id = ? ORDER BY timestamp ASC", [selectedPlaceId])
    : db.exec("SELECT raw_json FROM energy_telemetry WHERE period_type = 'daily' ORDER BY timestamp ASC");
  let dailyUsage: EnergyDataPoint[] = [];
  if (dailyRows.length > 0) {
    for (const row of dailyRows[0].values) {
      try {
        dailyUsage.push(JSON.parse(String(row[0])));
      } catch {
        // ignore
      }
    }
  }

  const monthlyRows = selectedPlaceId
    ? db.exec("SELECT raw_json FROM energy_telemetry WHERE period_type = 'monthly' AND place_id = ? ORDER BY timestamp ASC", [selectedPlaceId])
    : db.exec("SELECT raw_json FROM energy_telemetry WHERE period_type = 'monthly' ORDER BY timestamp ASC");
  let monthlyUsage: EnergyDataPoint[] = [];
  if (monthlyRows.length > 0) {
    for (const row of monthlyRows[0].values) {
      try {
        monthlyUsage.push(JSON.parse(String(row[0])));
      } catch {
        // ignore
      }
    }
  }

  return {
    account,
    places: finalPlaces,
    selectedPlaceId,
    invoices,
    hourlyUsage,
    dailyUsage,
    monthlyUsage,
    lastSyncAt,
    autoSyncEnabled
  };
}

// Save entire bundle to SQLite
export async function saveSqliteBundle(bundle: Partial<LocalSyncBundle>): Promise<void> {
  let db: Database;
  try {
    db = await getSqliteDatabase();
  } catch (err) {
    console.error('[SQLite WASM] saveSqliteBundle aborted due to DB init error:', err);
    throw new Error('Local database unavailable: synced data could not be saved.');
  }
  const now = new Date().toISOString();
  const activePlaceId = bundle.selectedPlaceId || bundle.account?.currentPlaceId || 'default';

  // Save Account
  if (bundle.account) {
    db.run(
      `INSERT OR REPLACE INTO accounts (account_number, account_holder, service_address, contract_capacity, payment_details, current_place_id, raw_json)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [
        bundle.account.accountNumber,
        bundle.account.accountHolder,
        bundle.account.serviceAddress,
        bundle.account.contractCapacity,
        bundle.account.paymentDetails,
        activePlaceId,
        JSON.stringify(bundle.account)
      ]
    );
  }

  // Save Places
  if (bundle.places && bundle.places.length > 0) {
    for (const p of bundle.places) {
      db.run(
        `INSERT OR REPLACE INTO contracted_places (id, demand_number, plan_name, status, address, contract_capacity, display_name, raw_json)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [p.id, p.demandNumber, p.planName, p.status || '', p.address, p.contractCapacity, p.displayName, JSON.stringify(p)]
      );
    }
  }

  // Save Invoices
  if (bundle.invoices && bundle.invoices.length > 0) {
    for (const inv of bundle.invoices) {
      db.run(
        `INSERT OR REPLACE INTO invoices (id, place_id, invoice_number, period_start, period_end, kwh_used, total_amount, due_date, status, raw_json)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          inv.id,
          activePlaceId,
          inv.invoiceNumber,
          inv.periodStart,
          inv.periodEnd,
          inv.kwhUsed,
          inv.totalAmount,
          inv.dueDate,
          inv.status,
          JSON.stringify(inv)
        ]
      );
    }
  }

  // Save Telemetry
  if (bundle.hourlyUsage && bundle.hourlyUsage.length > 0) {
    for (const h of bundle.hourlyUsage) {
      const datePart = (h.timestamp || h.label).replace(/[^a-zA-Z0-9]/g, '_');
      db.run(
        `INSERT OR REPLACE INTO energy_telemetry (id, place_id, period_type, label, timestamp, kwh, cost, is_peak, day_kwh, life_kwh, night_kwh, raw_json)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          `h_${datePart}_${activePlaceId}`,
          activePlaceId,
          'hourly',
          h.label,
          h.timestamp || null,
          h.kwh,
          h.cost,
          h.isPeak ? 1 : 0,
          h.dayKwh || 0,
          h.lifeKwh || 0,
          h.nightKwh || 0,
          JSON.stringify(h)
        ]
      );
    }
  }

  if (bundle.dailyUsage && bundle.dailyUsage.length > 0) {
    for (const d of bundle.dailyUsage) {
      db.run(
        `INSERT OR REPLACE INTO energy_telemetry (id, place_id, period_type, label, timestamp, kwh, cost, is_peak, day_kwh, life_kwh, night_kwh, raw_json)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          `d_${d.label}_${activePlaceId}`,
          activePlaceId,
          'daily',
          d.label,
          d.timestamp || null,
          d.kwh,
          d.cost,
          d.isPeak ? 1 : 0,
          d.dayKwh || 0,
          d.lifeKwh || 0,
          d.nightKwh || 0,
          JSON.stringify(d)
        ]
      );
    }
  }

  if (bundle.monthlyUsage) {
    // INSERT OR REPLACE never removes a row, so a rebuilt series shorter than the stored one -
    // exactly what happens once fabricated months stop being generated - has to delete the
    // leftovers or they are read back on every reload.
    const keepMonthlyIds = new Set(bundle.monthlyUsage.map((m) => `m_${m.label}_${activePlaceId}`));
    const storedMonthly = db.exec(
      "SELECT id FROM energy_telemetry WHERE period_type = 'monthly' AND place_id = ?",
      [activePlaceId]
    );
    for (const storedId of (storedMonthly[0]?.values || []).map((v) => String(v[0]))) {
      if (!keepMonthlyIds.has(storedId)) db.run('DELETE FROM energy_telemetry WHERE id = ?', [storedId]);
    }
    for (const m of bundle.monthlyUsage) {
      db.run(
        `INSERT OR REPLACE INTO energy_telemetry (id, place_id, period_type, label, timestamp, kwh, cost, is_peak, day_kwh, life_kwh, night_kwh, raw_json)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          `m_${m.label}_${activePlaceId}`,
          activePlaceId,
          'monthly',
          m.label,
          m.timestamp || null,
          m.kwh,
          m.cost,
          m.isPeak ? 1 : 0,
          m.dayKwh || 0,
          m.lifeKwh || 0,
          m.nightKwh || 0,
          JSON.stringify(m)
        ]
      );
    }
  }

  // App settings updates
  if (bundle.selectedPlaceId) {
    db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES ('selected_place_id', ?, ?)`, [bundle.selectedPlaceId, now]);
  }
  if (bundle.lastSyncAt) {
    db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES ('last_sync_at', ?, ?)`, [bundle.lastSyncAt, now]);
  }
  if (bundle.autoSyncEnabled !== undefined) {
    db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES ('auto_sync_enabled', ?, ?)`, [bundle.autoSyncEnabled ? 'true' : 'false', now]);
  }

  await persistDatabase();
}

// Language setting via SQLite
export async function getSqliteLanguage(): Promise<'ja' | 'en'> {
  try {
    const db = await getSqliteDatabase();
    const res = db.exec("SELECT value FROM app_settings WHERE key = 'lang'");
    if (res.length > 0 && res[0].values[0]) {
      const val = String(res[0].values[0][0]);
      if (val === 'en' || val === 'ja') return val;
    }
  } catch (err) {
    console.warn('[SQLite WASM] Failed to read lang from SQLite', err);
  }
  return 'ja';
}

export async function setSqliteLanguage(lang: 'ja' | 'en'): Promise<void> {
  const db = await getSqliteDatabase();
  db.run(`INSERT OR REPLACE INTO app_settings (key, value, updated_at) VALUES ('lang', ?, ?)`, [lang, new Date().toISOString()]);
  await persistDatabase();
}

// Biometric config via SQLite
export async function getSqliteBiometricConfig(): Promise<BiometricConfig> {
  try {
    const db = await getSqliteDatabase();
    const res = db.exec('SELECT enabled, biometric_type, pin_code, auto_lock_minutes FROM biometric_config WHERE id = ?', ['default']);
    if (res.length > 0 && res[0].values[0]) {
      const row = res[0].values[0];
      return {
        enabled: Boolean(row[0]),
        biometricType: (row[1] as BiometricConfig['biometricType']) || 'touch_id',
        pinCode: String(row[2] || '1234'),
        autoLockMinutes: Number(row[3] || 5)
      };
    }
  } catch (err) {
    console.warn('[SQLite WASM] Biometric read notice', err);
  }
  return {
    enabled: false,
    biometricType: 'touch_id',
    pinCode: '1234',
    autoLockMinutes: 5
  };
}

export async function saveSqliteBiometricConfig(cfg: BiometricConfig): Promise<void> {
  const db = await getSqliteDatabase();
  db.run(
    `INSERT OR REPLACE INTO biometric_config (id, enabled, biometric_type, pin_code, auto_lock_minutes)
     VALUES (?, ?, ?, ?, ?)`,
    ['default', cfg.enabled ? 1 : 0, cfg.biometricType, cfg.pinCode, cfg.autoLockMinutes]
  );
  await persistDatabase();
}

// Notifications via SQLite
export async function getSqliteNotifications(): Promise<InAppNotification[]> {
  try {
    const db = await getSqliteDatabase();
    const res = db.exec('SELECT id, title, body, type, timestamp, read_flag, amount FROM notifications ORDER BY timestamp DESC');
    if (res.length > 0) {
      return res[0].values.map((row) => ({
        id: String(row[0]),
        title: String(row[1]),
        body: String(row[2]),
        type: row[3] as InAppNotification['type'],
        timestamp: String(row[4]),
        read: Boolean(row[5]),
        amount: row[6] ? Number(row[6]) : undefined
      }));
    }
  } catch (err) {
    console.warn('[SQLite WASM] Notifications read notice', err);
  }

  return [];
}

export async function saveSqliteNotifications(notifs: InAppNotification[]): Promise<void> {
  const db = await getSqliteDatabase();
  for (const n of notifs) {
    db.run(
      `INSERT OR REPLACE INTO notifications (id, title, body, type, timestamp, read_flag, amount)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [n.id, n.title, n.body, n.type, n.timestamp, n.read ? 1 : 0, n.amount || null]
    );
  }
  await persistDatabase();
}

// Scraper credentials via SQLite
export async function getSqliteCredentials(): Promise<{ creds: PortalCredential; password: string }> {
  try {
    const db = await getSqliteDatabase();
    const res = db.exec('SELECT portal_url, dashboard_url, email, password FROM scraper_credentials WHERE id = ?', ['primary']);
    if (res.length > 0 && res[0].values[0]) {
      const row = res[0].values[0];
      const portalUrl = String(row[0] || 'https://www.symenergy.net/symenergy/login');
      const dashboardUrl = String(row[1] || 'https://www.symenergy.net/symenergy/dashboard');
      const email = String(row[2] || '');
      const password = String(row[3] || '');

      if (email || password) {
        return {
          creds: {
            portalUrl,
            dashboardUrl,
            email,
            rememberSession: true,
            lastSyncAt: new Date().toISOString(),
            syncIntervalMinutes: 60,
            sessionValid: Boolean(email && password)
          },
          password
        };
      }
    }

    // Check localStorage fallback in case SQLite was recently initialized or cleared
    if (typeof localStorage !== 'undefined') {
      const cachedCredsStr = localStorage.getItem('symenergy_portal_credentials_v3');
      const cachedPw = localStorage.getItem('symenergy_portal_password_v3') || '';
      if (cachedCredsStr) {
        try {
          const parsed = JSON.parse(cachedCredsStr);
          if (parsed && (parsed.email || cachedPw)) {
            // Restore to SQLite for persistence
            db.run(
              `INSERT OR REPLACE INTO scraper_credentials (id, portal_url, dashboard_url, email, password)
               VALUES (?, ?, ?, ?, ?)`,
              [
                'primary',
                parsed.portalUrl || 'https://www.symenergy.net/symenergy/login',
                parsed.dashboardUrl || 'https://www.symenergy.net/symenergy/dashboard',
                parsed.email || '',
                cachedPw
              ]
            );
            await persistDatabase();
            return {
              creds: {
                portalUrl: parsed.portalUrl || 'https://www.symenergy.net/symenergy/login',
                dashboardUrl: parsed.dashboardUrl || 'https://www.symenergy.net/symenergy/dashboard',
                email: parsed.email || '',
                rememberSession: true,
                lastSyncAt: new Date().toISOString(),
                syncIntervalMinutes: 60,
                sessionValid: Boolean(parsed.email && cachedPw)
              },
              password: cachedPw
            };
          }
        } catch {
          // Ignore JSON parse error
        }
      }
    }
  } catch (err) {
    console.warn('[SQLite WASM] Credentials read notice', err);
  }
  return {
    creds: {
      portalUrl: 'https://www.symenergy.net/symenergy/login',
      dashboardUrl: 'https://www.symenergy.net/symenergy/dashboard',
      email: '',
      rememberSession: false,
      lastSyncAt: '',
      syncIntervalMinutes: 60,
      sessionValid: false
    },
    password: ''
  };
}

export async function saveSqliteCredentials(creds: PortalCredential, password: string): Promise<void> {
  const db = await getSqliteDatabase();
  db.run(
    `INSERT OR REPLACE INTO scraper_credentials (id, portal_url, dashboard_url, email, password)
     VALUES (?, ?, ?, ?, ?)`,
    [
      'primary',
      creds.portalUrl || 'https://www.symenergy.net/symenergy/login',
      creds.dashboardUrl || 'https://www.symenergy.net/symenergy/dashboard',
      creds.email || '',
      password || ''
    ]
  );
  await persistDatabase();

  // Redundant synchronous cache to survive immediate browser refresh
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('symenergy_portal_credentials_v3', JSON.stringify(creds));
      localStorage.setItem('symenergy_portal_password_v3', password || '');
    }
  } catch (e) {
    console.warn('[SQLite WASM] localStorage creds sync warning:', e);
  }
}

// Scraper logs via SQLite
export async function getSqliteLogs(): Promise<ScrapeLogEntry[]> {
  try {
    const db = await getSqliteDatabase();
    const res = db.exec('SELECT id, timestamp, step, status, details FROM scraper_logs ORDER BY rowid DESC LIMIT 50');
    if (res.length > 0) {
      return res[0].values.map((row) => ({
        id: String(row[0]),
        timestamp: String(row[1]),
        step: String(row[2]),
        status: row[3] as ScrapeLogEntry['status'],
        details: String(row[4])
      }));
    }
  } catch (err) {
    console.warn('[SQLite WASM] Logs read notice', err);
  }
  return [];
}

export async function saveSqliteLogs(logs: ScrapeLogEntry[]): Promise<void> {
  const db = await getSqliteDatabase();
  for (const log of logs.slice(-50)) {
    db.run(
      `INSERT OR REPLACE INTO scraper_logs (id, timestamp, step, status, details)
       VALUES (?, ?, ?, ?, ?)`,
      [log.id, log.timestamp, log.step, log.status, log.details]
    );
  }
  await persistDatabase();
}

// Reset SQLite database to clean seed
export async function resetSqliteDatabase(): Promise<void> {
  const db = await getSqliteDatabase();
  db.run(`
    DELETE FROM accounts;
    DELETE FROM contracted_places;
    DELETE FROM invoices;
    DELETE FROM energy_telemetry;
    DELETE FROM notifications;
    DELETE FROM biometric_config;
    DELETE FROM scraper_credentials;
    DELETE FROM scraper_logs;
    DELETE FROM app_settings;
  `);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('symenergy_portal_credentials_v3');
      localStorage.removeItem('symenergy_portal_password_v3');
    }
  } catch {}
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
      const root = await navigator.storage.getDirectory();
      try {
        await root.removeEntry(OPFS_DB_FILE);
      } catch {}
    }
  } catch {}
  await seedInitialData(db);
  await persistDatabase();
}

// Download raw .sqlite binary file directly
export async function downloadSqliteDatabase(): Promise<void> {
  const db = await getSqliteDatabase();
  const binary = db.export();
  const blob = new Blob([binary as unknown as BlobPart], { type: 'application/x-sqlite3' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `symenergy_data_${new Date().toISOString().split('T')[0]}.sqlite`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Execute arbitrary SQL Query for Inspector / Debugging
export async function executeSqliteQuery(sql: string): Promise<QueryExecResult[]> {
  const db = await getSqliteDatabase();
  const result = db.exec(sql);
  // Auto-persist if it was an INSERT, UPDATE, DELETE, or CREATE
  if (/^\s*(INSERT|UPDATE|DELETE|CREATE|DROP|ALTER)/i.test(sql)) {
    await persistDatabase();
  }
  return result;
}

// Get SQLite Stats for UI
export async function getSqliteStats(): Promise<SQLiteStats> {
  try {
    const db = await getSqliteDatabase();
    const binary = db.export();

    const tableNames = [
      'accounts',
      'contracted_places',
      'invoices',
      'energy_telemetry',
      'notifications',
      'app_settings',
      'scraper_logs'
    ];

    const tables = tableNames.map((tbl) => {
      try {
        const res = db.exec(`SELECT COUNT(*) FROM ${tbl}`);
        const count = res.length > 0 && res[0].values[0] ? Number(res[0].values[0][0]) : 0;
        return { name: tbl, count };
      } catch {
        return { name: tbl, count: 0 };
      }
    });

    const versionRes = db.exec('SELECT sqlite_version()');
    const version = versionRes.length > 0 && versionRes[0].values[0] ? String(versionRes[0].values[0][0]) : '3.x WASM';
    const isOpfs = await checkOpfsSupport();

    return {
      ready: true,
      version: `SQLite ${version} (WebAssembly)`,
      sizeBytes: binary.byteLength,
      tables,
      lastPersistAt: lastPersistTimestamp,
      storageBackend: isOpfs ? 'OPFS (Origin Private File System)' : 'IndexedDB (Fallback)',
      storageLocation: isOpfs ? `opfs:///${OPFS_DB_FILE}` : `IndexedDB: ${IDB_NAME}/${IDB_STORE}`,
      isOpfsActive: isOpfs
    };
  } catch {
    const isOpfs = typeof navigator !== 'undefined' && !!navigator.storage?.getDirectory;
    return {
      ready: false,
      version: 'Initializing...',
      sizeBytes: 0,
      tables: [],
      lastPersistAt: lastPersistTimestamp,
      storageBackend: isOpfs ? 'OPFS (Origin Private File System)' : 'IndexedDB (Fallback)',
      storageLocation: isOpfs ? `opfs:///${OPFS_DB_FILE}` : `IndexedDB: ${IDB_NAME}/${IDB_STORE}`,
      isOpfsActive: isOpfs
    };
  }
}
